import { Box, Stack } from '@mui/joy';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Button, FormField } from 'core/components';
import { DB_DATE_FORMAT } from 'core/constants';
import { InputType } from 'core/enums';
import { FormProvider, useFormContext } from 'core/states';
import { useGetSubmitOrderLogReport } from 'dashboard/hooks';
import { Direction } from 'theme/enums';

import { submitOrderLogSchema } from './schema';
import { SubmitOrderLogTable } from './Table';
import { MAX_RANGE_DAYS } from './utils';
import { ReportError } from '../ReportError';

const DATE_FIELD_WIDTH = 170;
const TABLE_MIN_HEIGHT = 320;

// Month-to-date, matching the BE defaults for an unparameterised call.
const getDefaultRange = () => {
  const today = dayjs();

  return { dateFrom: today.startOf('month'), dateTo: today };
};

const SubmitOrderLogContent = () => {
  // The query runs off the last LOADED range, not the live form values: editing
  // the dates must not fire requests until Load applies them.
  const [requestPayload, setRequestPayload] = useState(() => {
    const { dateFrom, dateTo } = getDefaultRange();

    return {
      dateFrom: dateFrom.format(DB_DATE_FORMAT),
      dateTo: dateTo.format(DB_DATE_FORMAT),
    };
  });

  const { t } = useTranslation();

  const { control, errors, handleSubmit, setValue, watch } = useFormContext();

  const { user } = useAuthUser();

  const { report, isLoading, isFetching, isError, refetch } = useGetSubmitOrderLogReport(
    requestPayload,
    { enabled: !!user?.admin }
  );

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  // Day-granular key: `today` stays a stable reference within a day, so the
  // memos below don't recompute every render, while still advancing at midnight
  // so a dashboard left open across the day boundary can pick the new today.
  const todayKey = dayjs().format(DB_DATE_FORMAT);
  const today = useMemo(() => dayjs(todayKey), [todayKey]);

  const latestAllowedDateTo = useMemo(() => {
    const capEnd = dayjs(dateFrom).add(MAX_RANGE_DAYS - 1, 'day');

    return capEnd.isAfter(today) ? today : capEnd;
  }, [dateFrom, today]);

  // keepPreviousData keeps isLoading false on range changes and refetches, so
  // isFetching is what tells the grid to show a loading state instead of the
  // previous range's rows.
  const isDataLoading = isLoading || isFetching;

  // Only the To field is bounded by the other end; From stays free (up to today)
  // so the whole window can be moved back without first widening To.
  const onChangeDateFrom = useCallback(
    (value: Dayjs | null) => {
      if (!value) {
        return;
      }

      const capEnd = value.add(MAX_RANGE_DAYS - 1, 'day');
      const latest = capEnd.isAfter(today) ? today : capEnd;

      if (dayjs(dateTo).isBefore(value, 'day')) {
        setValue('dateTo', value);

        return;
      }

      if (dayjs(dateTo).isAfter(latest, 'day')) {
        setValue('dateTo', latest);
      }
    },
    [dateTo, setValue, today]
  );

  const onLoad = handleSubmit((data) => {
    const nextPayload = {
      dateFrom: data.dateFrom ? dayjs(data.dateFrom).format(DB_DATE_FORMAT) : undefined,
      dateTo: data.dateTo ? dayjs(data.dateTo).format(DB_DATE_FORMAT) : undefined,
    };

    // Re-submitting an unchanged range means "reload": the query key doesn't change,
    // so trigger the refetch directly.
    if (
      nextPayload.dateFrom === requestPayload.dateFrom &&
      nextPayload.dateTo === requestPayload.dateTo
    ) {
      refetch();

      return;
    }

    setRequestPayload(nextPayload);
  });

  return (
    <Stack sx={{ flexGrow: 1, gap: 2 }}>
      {/* Report scope (which import window) — not row filters: those live in the
          table's own column header filters. */}
      <Stack
        direction={Direction.ROW}
        sx={{ alignItems: 'flex-end', gap: 2, justifyContent: 'flex-end', paddingRight: 1 }}
      >
        <Box sx={{ width: DATE_FIELD_WIDTH }}>
          <FormField
            name="dateFrom"
            type={InputType.DATE_INPUT}
            inputProps={{
              control,
              error: errors?.dateFrom,
              label: t('labelDateFrom'),
              maxDate: today,
              isClearable: false,
              onChange: onChangeDateFrom,
            }}
          />
        </Box>

        <Box sx={{ width: DATE_FIELD_WIDTH }}>
          <FormField
            name="dateTo"
            type={InputType.DATE_INPUT}
            inputProps={{
              control,
              error: errors?.dateTo,
              label: t('labelDateTo'),
              minDate: dayjs(dateFrom),
              maxDate: latestAllowedDateTo,
              isClearable: false,
            }}
          />
        </Box>

        <Button text={t('buttonLabelLoad')} loading={isDataLoading} onClick={onLoad} />
      </Stack>

      {isError && (
        <ReportError
          title={t('errorTitleSubmitOrderLogReport')}
          message={t('messageSubmitOrderLogReportError')}
          onRetry={refetch}
        />
      )}

      {!isError && (
        /* Fill the viewport leftover; on short viewports keep a usable grid height
           and let the (scrollable) page layout take over. */
        <Box sx={{ flexGrow: 1, minHeight: TABLE_MIN_HEIGHT }}>
          <SubmitOrderLogTable entries={report?.entries ?? []} isLoading={isDataLoading} />
        </Box>
      )}
    </Stack>
  );
};

export const SubmitOrderLog = () => {
  const defaultValues = useMemo(() => getDefaultRange(), []);

  return (
    <FormProvider schema={submitOrderLogSchema()} defaultValues={defaultValues}>
      <SubmitOrderLogContent />
    </FormProvider>
  );
};
